/**
 *  SimpleAreaChart script file
 **/
import * as d3 from 'd3'
import customFormatters from '@/filters/customFormatters'
import i18n from '@/i18n'

const OPACITY = [0.24, 0.32, 0.4, 0.48]
const AREA_LABEL_TITLE = [
  i18n.t('PLAY_COUNT'),
  i18n.t('ABANDON_BEFORE'),
  i18n.t('ABANDON_BEFORE'),
  i18n.t('ABANDON_BEFORE'),
  i18n.t('SEE_FULL_VIDEO')
]
const AREA_LABEL_TITLE_WRAP = ['', i18n.t('OF_FIRST_QUARTER'), i18n.t('OF_HALF'), i18n.t('OF_THIRD_QUARTER')]
const AREA_LABEL_POSITION_Y = [40, 145, 145, 145, 145]
const AREA_LABEL_POSITION_X = [10, -100, -100, -100, -30]
const AREA_LABEL_WRAP_POSITION_X = [10, -95, -65, -92, -30]

export default {
  name: 'SimpleAreaChart',
  mixins: [],
  components: {},
  props: {
    margin: {
      type: Object,
      default: () => {
        return {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      },
      required: false
    },
    chartData: {
      type: Array,
      required: true
    },
    color: {
      type: Array,
      required: true
    },
    labelLeft: {
      type: String,
      default: undefined,
      required: false
    }
  },
  data () {
    return {
      wrapper: null,
      svg: null,
      date: null,
      width: null,
      height: null,
      xScale: null,
      yScale: null,
      tooltipDate: null,
      crosshair: null,
      tooltipData: {
        key: '',
        value: ''
      },
      tooltipClass: ''
    }
  },
  mounted () {
    this.createChart()

    window.addEventListener('resize', this.clearChart, false)
  },
  computed: {},
  methods: {
    createChart () {
      if (!this.$refs.container) {
        return
      }

      // Data
      const total = this.chartData.reduce((acc, cur) => {
        return acc + cur.value
      }, 0)

      // Add first point to the initial data with the total value
      // TODO: pendig to retreive from api
      const data = [{
        name: 0,
        value: total,
        percentage: 100
      }, ...this.chartData]

      const wrapper = this.$refs.container.getBoundingClientRect()
      const svg = d3
        .select(this.$refs.svg)
        .append('svg')
        .attr('height', 220)
        .attr('width', wrapper.width)
      const strokeWidth = 1.5
      const margin = { top: 0, bottom: 20, left: 20, right: 80 }
      const chart = svg.append('g').attr('transform', `translate(${margin.left},0)`)
      const width = +svg.attr('width') - margin.left - margin.right - strokeWidth * 2
      const height = +svg.attr('height') - margin.top - margin.bottom
      const grp = chart
        .append('g')
        .attr('transform', `translate(-${margin.left - strokeWidth},-${margin.top})`)

      // Create scales
      const yScale = d3
        .scaleLinear()
        .range([height, 0])
        .domain([0, d3.max(data, dataPoint => dataPoint.value) + d3.max(data, dataPoint => dataPoint.value) * 0.05])
      const xScale = d3
        .scaleLinear()
        .range([0, width])
        .domain(d3.extent(data, dataPoint => dataPoint.name))

      const area = d3
        .area()
        .x(dataPoint => xScale(dataPoint.name))
        .y0(height)
        .y1(dataPoint => yScale(dataPoint.value))

      // Add diferent areas for each data point with different opacity
      data.forEach((from, index) => {
        const to = index !== data.length - 1 ? data[index + 1] : data[index]
        const opacity = OPACITY[index]
        const linePosition = ((width / 4) * index) - 1
        let subtitle = customFormatters.filters.formatNumber(index === 0 ? total : from.value / (total || 1) * 100)
        subtitle = index === 0 ? subtitle : `${subtitle}%`

        // Area path
        grp
          .append('path')
          .attr('transform', `translate(${margin.left},0)`)
          .datum([from, to])
          .style('fill', 'url(#svgGradient)')
          .attr('opacity', opacity)
          .attr('d', area)
          .on('mousemove', this.moveTooltip)
          .on('mouseout', this.hideTooltip)

        // Vertical line separator
        grp
          .append('line')
          .attr('transform', `translate(${margin.left},0)`)
          .attr('x1', linePosition)
          .attr('y1', 0)
          .attr('x2', linePosition)
          .attr('y2', height)
          .style('stroke', '#e4e5e8')

        // Add text title to each area
        grp
          .append('text')
          .attr('transform', `translate(${margin.left},0)`)
          .attr('x', linePosition + AREA_LABEL_POSITION_X[index] - (index === data.length - 1 ? 100 : 0))
          .attr('y', AREA_LABEL_POSITION_Y[index])
          .attr('class', `area-label-${index < data.length - 1 ? index : 'last'}`)
          .text(AREA_LABEL_TITLE[index])

        if (index > 0) {
          grp
            .append('text')
            .attr('transform', `translate(${margin.left},0)`)
            .attr('x', linePosition + AREA_LABEL_WRAP_POSITION_X[index])
            .attr('y', AREA_LABEL_POSITION_Y[index] + 15)
            .attr('class', `area-label-${index}`)
            .text(AREA_LABEL_TITLE_WRAP[index])
        }
        // Add text subtitle to each area
        grp
          .append('text')
          .attr('transform', `translate(${margin.left},0)`)
          .attr('x', linePosition + AREA_LABEL_POSITION_X[index] + (index === 0 ? 0 : index === data.length - 1 ? -70 : 40))
          .attr('y', index === 0 ? AREA_LABEL_POSITION_Y[index] + 20 : AREA_LABEL_POSITION_Y[index] + (index === data.length - 1 ? 35 : 40))
          .attr('class', `area-sub-label-${index}`)
          .text(subtitle)
      })

      // Add the X Axis
      const xValues = data.map(d => d.name)

      chart.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(${margin.left - 20}, ${height})`)
        .call(d3.axisBottom(xScale).tickValues(xValues))

      // Add total value to the tooltip
      const totalSum = data.reduce((total, dp) => +total + +dp.popularity, 0)
      d3.select('.tooltip .totalValue').text(totalSum)

      // Add gradient defs to svg
      const defs = svg.append('defs')
      const gradient = defs.append('linearGradient').attr('id', 'svgGradient')

      gradient
        .append('stop')
        .attr('class', 'start')
        .attr('stop-color', this.color[0])

      // Styles
      d3.selectAll('.area-chart-container .domain, .area-chart-container line').style('stroke', '#f2f2f4')
      d3.selectAll('.area-chart-container text').style('fill', '#9397a2')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '12px')
        .style('font-weight', '600')

      // First area label style
      d3.selectAll('.area-label-0, .area-label-1, .area-label-2, .area-label-3')
        .style('fill', '#383D4C')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '12px')
        .style('font-weight', '500')

      // Second area label style
      d3.select('.area-sub-label-0')
        .style('fill', '#383D4C')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '16px')
        .style('font-weight', '700')

      // second area label style
      d3.selectAll('.area-sub-label-1, .area-sub-label-2, .area-sub-label-3')
        .style('fill', '#383D4C')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '16px')
        .style('font-weight', '500')
      d3.select('.area-sub-label-4')
        .style('fill', '#16969d')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '32px')
        .style('font-weight', '700')

      // Last area label style
      d3.select('.area-label-last')
        .style('fill', '#16969d')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '12px')
        .style('font-weight', '500')
    },
    showTooltip (data, colorScale) {
      this.tooltipData = {
        label: data.name,
        value: data.values,
        color: colorScale(0)
      }

      this.$refs.tooltip.style.opacity = 1
    },
    moveTooltip () {
      const x = d3.event.pageX
      this.$refs.tooltip.style.left = x - 230 + 'px'
    },
    hideTooltip () {
      this.$refs.tooltip.style.opacity = 0
    },
    clearChart () {
      while (this.$refs.svg && this.$refs.svg.lastChild) {
        this.$refs.svg.removeChild(this.$refs.svg.lastChild)
      }

      this.createChart()
    }
  }
}
